import { createReducer, on } from '@ngrx/store';
import {
  DailyLineAllocationOverlapDto,
  DailyLineCalendarDto,
  DailyLineDto,
  DailyLineScopedBaseDto,
  DailyLineShiftDto,
  EligibleCrewDto,
} from '../../features/daily-line-schedule/models/daily-line-schedule.models';
import { DailyLineScheduleActions } from './daily-line-schedule.actions';

export interface DailyLineScheduleState {
  /** Bases from daily-line access; the assign form offers only these. */
  scopedBases: DailyLineScopedBaseDto[];
  /** True once the access request has settled, so an empty base list means "none assigned". */
  accessLoaded: boolean;
  accessError: string | null;
  calendar: DailyLineCalendarDto | null;
  lines: DailyLineDto[];
  shiftsByBase: Record<string, DailyLineShiftDto[]>;
  eligibleCrew: EligibleCrewDto[];
  search: string;
  query: { startDate: string; endDate: string } | null;
  loading: boolean;
  writing: boolean;
  error: string | null;
  overlapConflicts: DailyLineAllocationOverlapDto[];
}

const initialState: DailyLineScheduleState = {
  scopedBases: [],
  accessLoaded: false,
  accessError: null,
  calendar: null,
  lines: [],
  shiftsByBase: {},
  eligibleCrew: [],
  search: '',
  query: null,
  loading: false,
  writing: false,
  error: null,
  overlapConflicts: [],
};

export const dailyLineScheduleReducer = createReducer(
  initialState,
  on(DailyLineScheduleActions.loadAccess, (state) => ({
    ...state,
    accessLoaded: false,
    accessError: null,
  })),
  on(DailyLineScheduleActions.loadAccessSuccess, (state, { bases }) => ({
    ...state,
    scopedBases: bases,
    accessLoaded: true,
    accessError: null,
  })),
  on(DailyLineScheduleActions.loadAccessFailure, (state, { error }) => ({
    ...state,
    scopedBases: [],
    accessLoaded: true,
    accessError: error,
  })),
  on(DailyLineScheduleActions.load, (state, { startDate, endDate, search }) => ({
    ...state,
    loading: true,
    error: null,
    search: search ?? state.search,
    query: { startDate, endDate },
  })),
  on(DailyLineScheduleActions.loadSuccess, (state, { calendar, search }) => ({
    ...state,
    loading: false,
    writing: false,
    calendar,
    search,
    error: null,
  })),
  on(DailyLineScheduleActions.loadFailure, (state, { error }) => ({
    ...state,
    loading: false,
    writing: false,
    error,
  })),
  on(DailyLineScheduleActions.loadLinesSuccess, (state, { lines }) => ({
    ...state,
    lines,
  })),
  on(DailyLineScheduleActions.loadShiftsSuccess, (state, { base, shifts }) => ({
    ...state,
    shiftsByBase: { ...state.shiftsByBase, [base]: shifts },
  })),
  on(DailyLineScheduleActions.loadEligibleCrewSuccess, (state, { crew }) => ({
    ...state,
    eligibleCrew: crew,
  })),
  on(DailyLineScheduleActions.clearEligibleCrew, (state) => ({
    ...state,
    eligibleCrew: [],
  })),
  on(DailyLineScheduleActions.setSearch, (state, { search }) => ({
    ...state,
    search,
  })),
  on(
    DailyLineScheduleActions.createLine,
    DailyLineScheduleActions.setLineStatus,
    DailyLineScheduleActions.setShiftStatus,
    (state) => ({
      ...state,
      writing: true,
      error: null,
    })
  ),
  on(DailyLineScheduleActions.createLineSuccess, (state, { line }) => ({
    ...state,
    writing: false,
    lines: state.lines.some((item) => item.id === line.id)
      ? state.lines.map((item) => (item.id === line.id ? line : item))
      : [...state.lines, line],
  })),
  on(DailyLineScheduleActions.setLineStatusSuccess, (state, { line }) => ({
    ...state,
    writing: false,
    lines: state.lines.map((item) => (item.id === line.id ? line : item)),
  })),
  on(DailyLineScheduleActions.setShiftStatusSuccess, (state, { base, shift }) => ({
    ...state,
    writing: false,
    shiftsByBase: {
      ...state.shiftsByBase,
      [base]: (state.shiftsByBase[base] ?? []).map((item) =>
        item.code === shift.code ? shift : item
      ),
    },
  })),
  on(DailyLineScheduleActions.assign, (state) => ({
    ...state,
    writing: true,
    error: null,
    overlapConflicts: [],
  })),
  on(DailyLineScheduleActions.assignSuccess, (state) => ({
    ...state,
    writing: false,
    error: null,
    overlapConflicts: [],
  })),
  on(DailyLineScheduleActions.assignFailure, (state, { error, overlaps }) => ({
    ...state,
    writing: false,
    error,
    overlapConflicts: overlaps ?? [],
  })),
  on(DailyLineScheduleActions.changeShift, (state) => ({
    ...state,
    writing: true,
    error: null,
  })),
  on(DailyLineScheduleActions.changeShiftSuccess, (state) => ({
    ...state,
    writing: false,
    error: null,
  })),
  on(DailyLineScheduleActions.changeShiftFailure, (state, { error }) => ({
    ...state,
    writing: false,
    error,
  }))
);
